'use client';

import * as React from 'react';

/**
 * Extra values the opener hands to the content of a named Modal, e.g.
 * `{ index: 3 }` from a gallery photo. The Modal does not read it.
 */
export type ModalOpenPayload = Readonly<Record<string, unknown>>;

export type NamedModalSetOpen = (
  open: boolean,
  payload?: ModalOpenPayload,
) => void;

export interface NamedModalHost {
  register: (name: string, setOpen: NamedModalSetOpen) => () => void;
  /** Opens the named Modal. Its content reads `payload` via `useModalOpenPayload`. */
  open: (name: string, payload?: ModalOpenPayload) => boolean;
  close: (name: string) => boolean;
  /** Fired when a named Modal closes, including Escape, backdrop, and the close control. */
  notifyClosed: (name: string) => void;
  subscribeClosed: (listener: (name: string) => void) => () => void;
}

function normalizeName(name: string): string {
  return name.trim();
}

export function createNamedModalHost(): NamedModalHost {
  const registry = new Map<string, NamedModalSetOpen>();
  const closedListeners = new Set<(name: string) => void>();

  return {
    register(name, setOpen) {
      const key = normalizeName(name);
      if (!key) {
        return () => undefined;
      }

      const existing = registry.get(key);
      if (
        existing &&
        existing !== setOpen &&
        process.env.NODE_ENV !== 'production'
      ) {
        console.warn(
          `[ModalHost] replacing Modal registered as "${key}". A duplicate name leaves only the latest instance reachable.`,
        );
      }
      registry.set(key, setOpen);
      return () => {
        if (registry.get(key) === setOpen) {
          registry.delete(key);
        }
      };
    },
    open(name, payload) {
      const setOpen = registry.get(normalizeName(name));
      if (!setOpen) {
        return false;
      }
      if (payload === undefined) {
        setOpen(true);
      } else {
        setOpen(true, payload);
      }
      return true;
    },
    close(name) {
      const setOpen = registry.get(normalizeName(name));
      if (!setOpen) {
        return false;
      }
      setOpen(false);
      return true;
    },
    notifyClosed(name) {
      const key = normalizeName(name);
      if (!key) return;
      closedListeners.forEach((listener) => {
        listener(key);
      });
    },
    subscribeClosed(listener) {
      closedListeners.add(listener);
      return () => {
        closedListeners.delete(listener);
      };
    },
  };
}

const fallbackHost = createNamedModalHost();
const ModalHostContext = React.createContext<NamedModalHost | null>(null);

export const ModalHostProvider: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const host = React.useMemo(() => createNamedModalHost(), []);
  return (
    <ModalHostContext.Provider value={host}>
      {children}
    </ModalHostContext.Provider>
  );
};

ModalHostProvider.displayName = 'ModalHostProvider';

export function useModalHost(): NamedModalHost {
  return React.useContext(ModalHostContext) ?? fallbackHost;
}

export function useModalHostOptional(): NamedModalHost | null {
  return React.useContext(ModalHostContext);
}

/** What content inside a Modal can know about the Modal around it. */
export interface EnclosingModal {
  /** Values from the latest `open(name, payload)`. Unset for a trigger or plain open. */
  payload: ModalOpenPayload | undefined;
  /**
   * Whether the Modal is set to draw its own close (X), so content can skip
   * drawing another. Reflects the setting, not a temporary hide.
   */
  showsCloseButton: boolean;
  /** Hides the Modal's close while content shows its own way out. */
  setCloseButtonHidden: (hidden: boolean) => void;
  /** Hides the Modal's visible title; it still names the dialog. */
  setTitleHidden?: (hidden: boolean) => void;
  /**
   * Compound `Modal.Content` reports `showCloseButton={false}` here. Slotted
   * Modals leave this unset; their close is on the Modal itself.
   */
  setContentOmitsClose?: (omits: boolean) => void;
  /** Closes the Modal through the same path as its dialog reporting closed. */
  requestClose: () => void;
}

const EnclosingModalContext = React.createContext<EnclosingModal | undefined>(
  undefined,
);

export const EnclosingModalProvider = EnclosingModalContext.Provider;

/** The Modal this content renders in, or `undefined` outside any Modal. */
export function useEnclosingModal(): EnclosingModal | undefined {
  return React.useContext(EnclosingModalContext);
}

/** Payload the enclosing Modal was opened with, or `undefined`. */
export function useModalOpenPayload(): ModalOpenPayload | undefined {
  return React.useContext(EnclosingModalContext)?.payload;
}

/**
 * Hides the enclosing Modal's close while `hidden` is true and this content
 * is mounted, e.g. a view that offers Back instead. Escape still closes.
 * Only slotted Modals honour it; the compound API places its own close.
 */
export function useHideModalCloseButton(hidden: boolean): void {
  const setHidden = React.useContext(
    EnclosingModalContext,
  )?.setCloseButtonHidden;
  React.useLayoutEffect(() => {
    if (!setHidden) return undefined;
    setHidden(hidden);
    return () => setHidden(false);
  }, [hidden, setHidden]);
}

/**
 * Hides the enclosing Modal's visible title while `hidden` is true and this
 * content is mounted, e.g. a full-bleed view with its own header. Screen
 * readers still get the title as the dialog name. Only slotted fullscreen and
 * centered dialogs honour it.
 */
export function useHideModalTitle(hidden: boolean): void {
  const setHidden = React.useContext(EnclosingModalContext)?.setTitleHidden;
  React.useLayoutEffect(() => {
    if (!setHidden) return undefined;
    setHidden(hidden);
    return () => setHidden(false);
  }, [hidden, setHidden]);
}
